import type { ReactNode } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ApartmentIcon from "@mui/icons-material/Apartment";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useAuth0 } from "@auth0/auth0-react";
import type { PaletteMode } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { View } from "../types";

const SIDEBAR_WIDTH = 232;

const titles: Record<View, string> = {
  dashboard: "Dashboard",
  drivers: "Drivers",
  driver: "Driver Detail",
  tenants: "Tenants",
  tenant: "Tenant Detail",
  memberships: "Memberships",
  companies: "Company",
  api_keys: "API Keys",
};

interface NavItem {
  view: View;
  label: string;
  icon: ReactNode;
}

const items: NavItem[] = [
  { view: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
  { view: "companies", label: "Company", icon: <ApartmentIcon /> },
];

interface Props {
  view: View;
  onNavigate: (v: View, id?: number) => void;
  themeMode: PaletteMode;
  onToggleThemeMode: () => void;
  children: ReactNode;
}

export default function Layout({ view, onNavigate, themeMode, onToggleThemeMode, children }: Props) {
  const { user, logout } = useAuth0();
  const theme = useTheme();

  const navItemSx = {
    color: "rgba(255,255,255,0.85)",
    borderRadius: 2,
    mx: 1.25,
    "&.Mui-selected": {
      bgcolor: alpha(theme.palette.primary.main, 0.24),
      color: "#fff",
    },
    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) },
    "& .MuiListItemIcon-root": { color: "inherit", minWidth: 36 },
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: theme.palette.primary.dark,
          backgroundImage:
            "linear-gradient(180deg, rgba(47,128,255,0.12) 0%, rgba(11,31,58,1) 35%)",
          color: "#fff",
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Toolbar sx={{ px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Zennet Fleet
          </Typography>
        </Toolbar>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        <Box sx={{ flexGrow: 1 }}>
          <List dense>
            {items.map((i) => (
              <ListItemButton
                key={i.view}
                selected={view === i.view}
                onClick={() => onNavigate(i.view)}
                sx={navItemSx}
              >
                <ListItemIcon>{i.icon}</ListItemIcon>
                <ListItemText primary={i.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* User profile + logout */}
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={user?.picture}
            alt={user?.name}
            sx={{ width: 32, height: 32, fontSize: 14 }}
          >
            {user?.name?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
            <Typography variant="body2" noWrap sx={{ color: "#fff", fontWeight: 500, lineHeight: 1.2 }}>
              {user?.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.5)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {user?.email}
            </Typography>
          </Box>
          <Tooltip title="Sign out">
            <LogoutIcon
              fontSize="small"
              sx={{ color: "rgba(255,255,255,0.5)", cursor: "pointer", "&:hover": { color: "#fff" } }}
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: `${SIDEBAR_WIDTH}px`,
          p: 4,
          bgcolor: "background.default",
        }}
      >
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
            {titles[view]}
          </Typography>
          <Tooltip title={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton
              size="small"
              onClick={onToggleThemeMode}
              sx={{ color: theme.palette.mode === "dark" ? "warning.main" : "text.primary" }}
            >
              {themeMode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        {children}
      </Box>
    </Box>
  );
}
